import type { ProjectionModel } from "./model";
import type {
  ClinicAgeBand,
  ClinicRateTable,
  ClinicSingleRate,
  ClinicTreatmentSuccessInput,
  ClinicProjectionDetails,
  ClinicRateObservation,
  FertilityProjectionResult,
  ObservedValue,
  ProjectionEstimate,
  ProjectionInput,
  ProjectionModelMetadata,
  ProjectionStage,
  ProjectionStatus,
  RateEstimate,
  ProjectionAdjustment,
} from "./types";

export const CLINIC_SUCCESS_MODEL_METADATA: ProjectionModelMetadata = {
  id: "clinic-adjusted-treatment-success",
  version: "1.0.0",
  reviewStatus: "pending_clinical_review" as const,
  population:
    "Clinic-reported treatment outcomes with explicit treatment, population, denominator, and reporting-year context",
  validationScope:
    "Clinic-adjusted live-birth estimates; source denominators and patient modifiers must remain separate and reviewable",
  sourceIds: [
    "cdc-nass-2022-final-art-success-rates",
    "cdc-nass-2022-services-and-profiles",
    "sart-national-summary-2024",
    "asrm-embryo-vitrification-2021",
    "mclernon-ivf-live-birth-2016",
    "rittenberg-bmi-ivf-2011",
    "waylen-smoking-art-2009",
  ],
  parameterSourceIds: [
    "cdc-nass-2022-final-art-success-rates",
    "mclernon-ivf-live-birth-2016",
    "rittenberg-bmi-ivf-2011",
    "waylen-smoking-art-2009",
  ],
  supportedFeatureIds: [
    "age",
    "clinic_rates",
    "medical_factors",
    "cycle_counts",
  ],
  supportedGoalKinds: ["probability_at_least_one_live_birth"],
  priority: 70,
  estimateClass: "personalized" as const,
};

type AgeBand = ClinicAgeBand;

const DIAGNOSIS_ODDS_RATIOS: Record<string, number> = {
  pcos: 1.15,
  male_factor: 1.08,
  endometriosis: 0.85,
  diminished_ovarian_reserve: 0.7,
  tubal_factor: 0.92,
  uterine_factor: 0.8,
};

const IVF_OUTCOME_SOURCE_ID = "mclernon-ivf-live-birth-2016";
const BMI_OUTCOME_SOURCE_ID = "rittenberg-bmi-ivf-2011";
const SMOKING_OUTCOME_SOURCE_ID = "waylen-smoking-art-2009";

export const CLINIC_SUCCESS_MODEL: ProjectionModel = {
  metadata: CLINIC_SUCCESS_MODEL_METADATA,
  project(input, now = new Date()) {
    const clinic = input.clinic;
    const generatedAt = now.toISOString();
    const age = validAge(
      input.patient.ageAtTransfer ?? input.patient.ageAtRetrieval,
    );
    const modifiers = clinic?.patientModifiers;
    const priorFailedCycles = validCount(modifiers?.priorFailedCycles) ?? 0;
    const normalizedInputs: Record<string, number | string | null> = {
      kind: input.kind,
      treatmentType: clinic?.treatmentType ?? null,
      population: clinic?.population ?? null,
      embryoSource: clinic?.embryoSource ?? null,
      age: age,
      reportYear: clinic?.reportYear ?? null,
      reportingPopulation: clinic?.reportingPopulation ?? null,
      denominator: clinic?.denominator?.value ?? null,
      denominatorDescription: clinic?.denominatorDescription ?? null,
      outcomeMetric: clinic?.outcomeMetric ?? null,
      cyclesPerYear: clinic?.cyclesPerYear?.value ?? null,
      priorFailedCycles,
    };

    if (input.context?.consentState === "withheld") {
      return result(
        input,
        generatedAt,
        normalizedInputs,
        [],
        [],
        "not_applicable",
        false,
        undefined,
        "Projection not calculated because projection consent was withheld.",
      );
    }

    if (
      input.goal &&
      input.goal.kind !== "probability_at_least_one_live_birth"
    ) {
      return result(
        input,
        generatedAt,
        normalizedInputs,
        [],
        ["clinic adapter supports one-live-birth probability only"],
        "insufficient_data",
        false,
      );
    }

    const missingInputs: string[] = [];
    if (!clinic) missingInputs.push("clinic treatment success inputs");
    if (!clinic?.outcomeMetric || clinic.outcomeMetric === "unknown") {
      missingInputs.push("clinic outcome metric");
    }
    if (clinic && !clinic.reportYear) {
      missingInputs.push("clinic outcome report year");
    }
    if (clinic && !clinic.reportingPopulation) {
      missingInputs.push("clinic reporting population");
    }
    if (clinic?.population === "own_eggs" && age == null) {
      missingInputs.push("patient age for the clinic rate band");
    }

    const selected = clinic ? selectRate(clinic, age, priorFailedCycles) : null;
    if (!selected) missingInputs.push("applicable clinic treatment rate");

    const denominator =
      selected?.denominator ?? validPositiveInteger(clinic?.denominator);
    if (selected && !denominator) {
      missingInputs.push("clinic treatment outcome denominator");
    }

    const stages: ProjectionStage[] = [];
    let estimate: ProjectionEstimate | null = null;
    let clinicDetails: ClinicProjectionDetails | undefined;
    if (selected && denominator) {
      const adjusted = adjustRate(selected.rate, modifiers);
      const interval = estimateInterval(adjusted, denominator);
      estimate = interval;
      clinicDetails = {
        baseRate: {
          mean: selected.rate.mean * 100,
          low: selected.rate.low * 100,
          high: selected.rate.high * 100,
          unit: "percentage",
        },
        baseRateLabel: selected.label,
        adjustments: adjusted.adjustments,
        dataQuality: dataQuality(denominator),
      };
      stages.push({
        id: "live_births",
        basis: "modeled",
        estimate,
        rate: adjusted,
        sourceIds: adjusted.sourceIds,
      });
    }

    const modeled = stages.length > 0;
    const status: ProjectionStatus =
      stages.length === 0
        ? "insufficient_data"
        : missingInputs.length > 0
          ? "partial"
          : "not_reviewed";

    return {
      ...result(
        input,
        generatedAt,
        normalizedInputs,
        stages,
        missingInputs,
        status,
        modeled,
        clinicDetails,
      ),
      probabilityAtLeastOneLiveBirth: estimate,
    };
  },
};

function result(
  input: ProjectionInput,
  generatedAt: string,
  normalizedInputs: Record<string, number | string | null>,
  stages: ProjectionStage[],
  missingInputs: string[],
  status: ProjectionStatus,
  modeled: boolean,
  clinicDetails?: ClinicProjectionDetails,
  extraWarning?: string,
): FertilityProjectionResult {
  const sourceIds = [
    ...(input.evidence?.map((source) => source.id) ?? []),
    ...(input.clinic ? clinicSourceIds(input.clinic) : []),
    ...stages.flatMap((stage) => stage.sourceIds),
  ];
  return {
    contractVersion: "1",
    status,
    presentation: modeled ? "blocked_pending_review" : "patient_safe",
    kind: input.kind,
    model: CLINIC_SUCCESS_MODEL_METADATA,
    generatedAt,
    asOf: input.asOf ?? null,
    normalizedInputs,
    missingInputs: [...new Set(missingInputs)],
    assumptions: [
      "Clinic-reported rates are not interchangeable across treatment types, donor status, fresh/frozen status, or reporting populations.",
      "Patient modifiers are applied in odds space and remain separate from the clinic-reported base rate.",
      "A selected clinic rate cell without an explicit outcome denominator is not used to create a patient-facing estimate; annual clinic volume is descriptive only.",
    ],
    warnings: [
      ...(modeled
        ? [
            "The clinic-adjusted treatment adapter is pending clinical/product review and must remain gated before patient-facing use.",
          ]
        : []),
      ...(extraWarning ? [extraWarning] : []),
    ],
    stages,
    uncertainty: {
      kind: "interval",
      method: "provided-by-model",
      confidenceLevel: 0.95,
      note: "The interval uses a binomial approximation only with the selected population's observed outcome denominator.",
    },
    expectedLiveBirths: null,
    probabilityAtLeastOneLiveBirth: null,
    clinicDetails,
    sourceIds: [...new Set(sourceIds)],
    disclaimer:
      "This is a population- and clinic-level estimate, not a diagnosis, treatment recommendation, or guarantee of pregnancy or live birth.",
  };
}

function selectRate(
  clinic: ClinicTreatmentSuccessInput,
  age: number | null,
  priorFailedCycles: number,
): { rate: RateEstimate; label: string; denominator: number | null } | null {
  if (clinic.population !== "own_eggs") {
    const selected = donorRateFor(clinic);
    if (!selected || !rateContextMatches(selected, clinic)) return null;
    const rate = percentageRate(selected.rate);
    return rate
      ? {
          rate,
          denominator: validPositiveInteger(selected.rate.denominator),
          label:
            clinic.population === "donated_embryos"
              ? "clinic donated-embryo treatment rate"
              : "clinic donor-egg treatment rate",
        }
      : null;
  }

  if (age == null) return null;
  const band = ageBand(age);
  const tables =
    priorFailedCycles === 0
      ? [clinic.firstCycleOwnEggRates, clinic.ownEggRates]
      : [clinic.ownEggRates];
  const table = tables.find(
    (candidate): candidate is NonNullable<typeof candidate> =>
      Boolean(candidate && rateContextMatches(candidate, clinic)),
  );
  if (!table) return null;
  const value = table.rates[band];
  const rate = value ? percentageRate(value) : null;
  return rate
    ? {
        rate,
        denominator: value ? validPositiveInteger(value.denominator) : null,
        label:
          table === clinic.firstCycleOwnEggRates
            ? "clinic first-cycle own-egg treatment rate"
            : "clinic own-egg treatment rate",
      }
    : null;
}

function adjustRate(
  base: RateEstimate,
  modifiers: ClinicTreatmentSuccessInput["patientModifiers"],
): RateEstimate & { adjustments: ProjectionAdjustment[] } {
  let mean = toOdds(base.mean);
  let low = toOdds(base.low);
  let high = toOdds(base.high);
  const sourceIds = [...base.sourceIds];
  const adjustments: ProjectionAdjustment[] = [];
  const apply = (
    oddsRatio: number,
    observed: ObservedValue<unknown> | undefined,
    modelSourceIds: string[] = [],
  ) => {
    mean *= oddsRatio;
    low *= oddsRatio;
    high *= oddsRatio;
    sourceIds.push(...modelSourceIds);
    if (observed?.provenance.sourceId) {
      sourceIds.push(observed.provenance.sourceId);
    }
  };

  const applyNamed = (
    factor: string,
    oddsRatio: number,
    observed?: ObservedValue<unknown>,
    modelSourceIds: string[] = [],
  ) => {
    apply(oddsRatio, observed, modelSourceIds);
    adjustments.push({
      factor,
      direction:
        oddsRatio > 1 ? "increase" : oddsRatio < 1 ? "decrease" : "neutral",
      oddsRatio,
      sourceIds: [
        ...modelSourceIds,
        ...(observed?.provenance.sourceId
          ? [observed.provenance.sourceId]
          : []),
      ],
    });
  };

  for (const diagnosis of modifiers?.diagnoses?.value ?? []) {
    const oddsRatio = DIAGNOSIS_ODDS_RATIOS[diagnosis];
    if (oddsRatio) {
      applyNamed(diagnosis, oddsRatio, modifiers?.diagnoses, [
        IVF_OUTCOME_SOURCE_ID,
      ]);
    }
  }
  if (modifiers?.bmi?.value === "overweight") {
    applyNamed("BMI (overweight)", 0.9, modifiers.bmi, [BMI_OUTCOME_SOURCE_ID]);
  }
  if (modifiers?.bmi?.value === "underweight") {
    applyNamed("BMI (underweight)", 0.85, modifiers.bmi, [
      BMI_OUTCOME_SOURCE_ID,
    ]);
  }
  if (modifiers?.smoking?.value === "current") {
    applyNamed("Active smoking", 0.72, modifiers.smoking, [
      SMOKING_OUTCOME_SOURCE_ID,
    ]);
  }
  if (modifiers?.smoking?.value === "former") {
    applyNamed("Former smoking", 0.9, modifiers.smoking, [
      SMOKING_OUTCOME_SOURCE_ID,
    ]);
  }
  if (modifiers?.priorLiveBirth?.value) {
    applyNamed("Prior live birth", 1.2, modifiers.priorLiveBirth, [
      IVF_OUTCOME_SOURCE_ID,
    ]);
  }
  if (!modifiers?.priorLiveBirth?.value && modifiers?.priorMiscarriage?.value) {
    applyNamed("Prior pregnancy", 1.05, modifiers.priorMiscarriage, [
      IVF_OUTCOME_SOURCE_ID,
    ]);
  }
  if (priorFailedCyclesValue(modifiers) > 0) {
    applyNamed(
      `${priorFailedCyclesValue(modifiers)} prior failed cycle${priorFailedCyclesValue(modifiers) > 1 ? "s" : ""}`,
      Math.pow(0.92, Math.min(priorFailedCyclesValue(modifiers), 4)),
      modifiers?.priorFailedCycles,
      [IVF_OUTCOME_SOURCE_ID],
    );
  }

  return {
    mean: fromOdds(mean),
    low: fromOdds(Math.min(low, high)),
    high: fromOdds(Math.max(low, high)),
    unit: "probability",
    sourceIds: [...new Set(sourceIds)],
    adjustments,
  };
}

function donorRateFor(
  clinic: ClinicTreatmentSuccessInput,
): ClinicSingleRate | undefined {
  if (clinic.population === "donated_embryos") {
    return clinic.donorRates?.donatedEmbryos;
  }
  if (clinic.treatmentType === "frozen_embryo_transfer") {
    return clinic.embryoSource === "frozen_embryos"
      ? clinic.donorRates?.frozenEmbryos
      : undefined;
  }
  if (clinic.embryoSource === "fresh_from_fresh_eggs") {
    return clinic.donorRates?.freshEmbryosFreshEggs;
  }
  if (clinic.embryoSource === "fresh_from_frozen_eggs") {
    return clinic.donorRates?.freshEmbryosFrozenEggs;
  }
  return undefined;
}

function rateContextMatches(
  rate: ClinicRateTable | ClinicSingleRate,
  clinic: ClinicTreatmentSuccessInput,
): boolean {
  return (
    rate.treatmentType === clinic.treatmentType &&
    rate.population === clinic.population &&
    rate.embryoSource === clinic.embryoSource &&
    rate.metric === clinic.outcomeMetric
  );
}

function estimateInterval(
  rate: RateEstimate,
  denominator: number | null,
): ProjectionEstimate {
  if (!denominator) {
    return {
      mean: rate.mean,
      low: rate.low,
      high: rate.high,
      unit: "probability",
    };
  }
  const margin = 1.96 * Math.sqrt((rate.mean * (1 - rate.mean)) / denominator);
  return {
    mean: rate.mean,
    low: Math.max(0, Math.min(rate.low, rate.mean - margin)),
    high: Math.min(1, Math.max(rate.high, rate.mean + margin)),
    unit: "probability",
  };
}

function percentageRate(observed: ClinicRateObservation): RateEstimate | null {
  if (
    !Number.isFinite(observed.value) ||
    observed.value < 0 ||
    observed.value > 100
  ) {
    return null;
  }
  const value = observed.value / 100;
  return {
    mean: value,
    low: value,
    high: value,
    unit: "probability",
    sourceIds: [
      ...(observed.provenance.sourceId ? [observed.provenance.sourceId] : []),
      ...(observed.denominator?.provenance.sourceId
        ? [observed.denominator.provenance.sourceId]
        : []),
    ],
  };
}

function ageBand(age: number): AgeBand {
  if (age < 35) return "under35";
  if (age <= 37) return "between35And37";
  if (age <= 40) return "between38And40";
  if (age <= 42) return "between41And42";
  return "over42";
}

function validAge(observed: ObservedValue<number> | undefined): number | null {
  return observed &&
    Number.isFinite(observed.value) &&
    observed.value >= 18 &&
    observed.value <= 60
    ? observed.value
    : null;
}

function validPositiveInteger(
  observed: ObservedValue<number> | undefined,
): number | null {
  return observed && Number.isInteger(observed.value) && observed.value > 0
    ? observed.value
    : null;
}

function validCount(
  observed: ObservedValue<number> | undefined,
): number | null {
  return observed &&
    Number.isFinite(observed.value) &&
    Number.isInteger(observed.value) &&
    observed.value >= 0
    ? observed.value
    : null;
}

function priorFailedCyclesValue(
  modifiers: ClinicTreatmentSuccessInput["patientModifiers"],
): number {
  return validCount(modifiers?.priorFailedCycles) ?? 0;
}

function toOdds(probability: number): number {
  if (probability <= 0) return 0;
  if (probability >= 1) return Number.MAX_VALUE;
  const safe = Math.min(0.999999, Math.max(0.000001, probability));
  return safe / (1 - safe);
}

function fromOdds(odds: number): number {
  if (odds <= 0) return 0;
  if (!Number.isFinite(odds)) return 1;
  return Math.min(0.999999, Math.max(0.000001, odds / (1 + odds)));
}

function clinicSourceIds(clinic: ClinicTreatmentSuccessInput): string[] {
  const values = [
    clinic.cyclesPerYear,
    clinic.denominator,
    clinic.patientModifiers?.diagnoses,
    clinic.patientModifiers?.bmi,
    clinic.patientModifiers?.smoking,
    clinic.patientModifiers?.priorLiveBirth,
    clinic.patientModifiers?.priorMiscarriage,
    clinic.patientModifiers?.priorFailedCycles,
  ];
  return values.flatMap((value) =>
    value?.provenance.sourceId ? [value.provenance.sourceId] : [],
  );
}

function dataQuality(denominator: number | null): "high" | "moderate" | "low" {
  if (!denominator) return "low";
  if (denominator >= 200) return "high";
  if (denominator >= 50) return "moderate";
  return "low";
}
